(() => {
  if (window.__WN_PRODUCT_GALLERY_V3__) return;
  window.__WN_PRODUCT_GALLERY_V3__ = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mod = (value, length) => ((value % length) + length) % length;

  class WnProductGallery {
    constructor(root) {
      this.root = root;
      this.form = root.closest('wn-product-form');
      this.stage = root.querySelector('[data-media-stage]');
      this.track = root.querySelector('[data-wn-gallery-track]');
      this.surface = this.track || this.stage;
      this.panels = [...root.querySelectorAll('[data-media-panel]')];
      this.thumbs = [...root.querySelectorAll('[data-media-target]')];
      this.bubbles = [...root.querySelectorAll('[data-wn-bubble]')];
      this.previous = root.querySelector('[data-wn-gallery-previous]');
      this.next = root.querySelector('[data-wn-gallery-next]');
      this.counter = root.querySelector('[data-wn-gallery-counter]');
      this.activeIndex = Math.max(0, this.panels.findIndex((panel) => panel.classList.contains('is-active') && !panel.hidden));
      this.pointer = null;
      this.scrollFrame = 0;
      this.suppressClickUntil = 0;
      this.abortController = new AbortController();
      this.lightbox = null;
      if (!this.stage || !this.surface || !this.panels.length) return;
      this.setup();
    }

    setupThumbnailNavigation(signal) {
      this.thumbRail = this.root.querySelector('[data-wn-thumb-rail]');
      const rail = this.thumbRail;
      if (!rail) return;
      const prev = this.root.querySelector('[data-wn-thumb-prev]');
      const next = this.root.querySelector('[data-wn-thumb-next]');
      rail.querySelectorAll('img').forEach(image => {
        image.draggable = false;
        image.addEventListener('dragstart', event => event.preventDefault(), { signal });
      });
      const update = () => {
        const overflow = rail.scrollWidth - rail.clientWidth > 4;
        if (prev) prev.hidden = !overflow || rail.scrollLeft <= 4;
        if (next) next.hidden = !overflow || rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      };
      this.refreshThumbnailNav = update;
      const move = direction => rail.scrollBy({ left: direction * Math.max(rail.clientWidth * .65, 240), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      prev?.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); move(-1); }, { signal });
      next?.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); move(1); }, { signal });
      rail.addEventListener('scroll', update, { passive: true, signal });
      rail.addEventListener('wheel', event => {
        const raw = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rail.clientWidth : 1;
        const amount = raw * unit * 2.6;
        if (rail.scrollWidth <= rail.clientWidth + 4) return;
        if ((amount < 0 && rail.scrollLeft <= 0) || (amount > 0 && rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2)) return;
        event.preventDefault();
        rail.scrollBy({ left: amount, behavior: 'instant' });
      }, { passive: false, signal });
      let drag = null;
      this.root.addEventListener('selectstart', event => { if (event.target.closest('.wn-gallery-thumbnails, [data-media-stage]')) event.preventDefault(); }, { signal });
      rail.addEventListener('selectstart', event => event.preventDefault(), { signal });
      rail.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        drag = { id:event.pointerId, x:event.clientX, scroll:rail.scrollLeft, moved:false };
        if (event.cancelable) event.preventDefault();
      }, { signal });
      document.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (Math.abs(event.clientX - drag.x) > 5) drag.moved = true;
        if (!drag.moved) return;
        event.preventDefault();
        // Pointer moves are tracked at document level.
        rail.classList.add('is-dragging');
        rail.scrollLeft = drag.scroll + drag.x - event.clientX;
      }, { signal });
      const finish = event => {
        if (!drag || (event && event.pointerId !== drag.id)) return;
        if (drag.moved) this.suppressThumbClickUntil = performance.now() + 400;
        try { if (rail.hasPointerCapture?.(drag.id)) rail.releasePointerCapture(drag.id); } catch (_) {}
        drag = null;
        rail.classList.remove('is-dragging');
        update();
      };
      document.addEventListener('pointerup', finish, { signal });
      document.addEventListener('pointercancel', finish, { signal });
      if (window.ResizeObserver) {
        const resize = new ResizeObserver(update);
        resize.observe(rail);
        signal.addEventListener('abort', () => resize.disconnect(), { once:true });
      }
      requestAnimationFrame(update);
    }

    setup() {
      const { signal } = this.abortController;
      this.setupThumbnailNavigation(signal);
      this.stage.setAttribute('role', 'region');
      this.stage.setAttribute('aria-label', window.WN_I18N?.product?.images || 'Product images');
      if (!this.stage.hasAttribute('tabindex')) this.stage.tabIndex = 0;

      this.surface.style.touchAction = this.track ? 'pan-x pan-y' : 'pan-y';
      this.surface.style.userSelect = 'none';
      this.surface.style.webkitUserSelect = 'none';
      this.root.querySelectorAll('.wn-product-gallery__image').forEach((image) => {
        image.draggable = false;
        image.style.webkitUserDrag = 'none';
        image.style.userSelect = 'none';
        image.style.webkitUserSelect = 'none';
        image.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      });
      this.surface.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

      if (this.track) {
        this.panels.forEach((panel) => {
          panel.hidden = false;
          panel.setAttribute('aria-hidden', 'false');
        });
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => this.syncFromScroll());
        }, { passive: true, signal });
      }

      this.surface.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.surface.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
      this.surface.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.surface.addEventListener('pointercancel', () => this.finishPointer(), { signal });
      this.previous?.addEventListener('click', (event) => { event.preventDefault(); this.go(this.activeIndex - 1); }, { signal });
      this.next?.addEventListener('click', (event) => { event.preventDefault(); this.go(this.activeIndex + 1); }, { signal });
      this.root.addEventListener('click', (event) => this.onClick(event), { capture: true, signal });
      this.surface.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

      this.form?.addEventListener('wn:product:variant-change', (event) => this.onVariantChange(event), { signal });
      this.form?.addEventListener('wn:product:media-change-request', (event) => {
        const index = this.indexForMedia(event.detail?.mediaId);
        if (index < 0) return;
        event.preventDefault();
        this.go(index);
      }, { signal });

      document.addEventListener('click', (event) => {
        if (!event.target.closest('[data-wn-bubble]')) this.closeBubbles();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeBubbles();
          if (this.lightbox?.open) this.closeLightbox();
        }
      }, { signal });

      this.setupLightbox(signal);
      this.sync(this.activeIndex, false);
      if (this.track) requestAnimationFrame(() => this.scrollToIndex(this.activeIndex, false));
    }

    indexForMedia(mediaId) {
      const id = String(mediaId || '');
      return this.panels.findIndex((panel) => String(panel.dataset.mediaPanel || '') === id);
    }

    onVariantChange(event) {
      if (event.detail?.preserveMedia) return;
      const index = this.indexForMedia(event.detail?.variant?.featured_media_id);
      if (index >= 0) this.go(index);
    }

    go(index, emitChange = true) {
      if (!this.panels.length) return;
      const nextIndex = this.panels.length > 1 ? mod(Number(index) || 0, this.panels.length) : 0;
      if (this.track) this.scrollToIndex(nextIndex, false);
      this.sync(nextIndex, emitChange);
      if (this.lightbox?.open) this.updateLightbox();
    }

    scrollToIndex(index, smooth = true) {
      if (!this.track) return;
      const panel = this.panels[index];
      if (!panel) return;
      this.track.scrollTo({ left: panel.offsetLeft, behavior: 'auto' });
    }

    sync(index, emitChange = true) {
      this.activeIndex = index;
      const activePanel = this.panels[index];
      this.panels.forEach((panel, panelIndex) => {
        const selected = panelIndex === index;
        panel.classList.toggle('is-active', selected);
        if (!this.track) {
          panel.hidden = !selected;
          panel.setAttribute('aria-hidden', String(!selected));
        }
      });
      this.thumbs.forEach((thumb) => {
        const selected = String(thumb.dataset.mediaTarget || '') === String(activePanel?.dataset.mediaPanel || '');
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-current', String(selected));
        if (selected && this.thumbRail && (thumb.offsetLeft < this.thumbRail.scrollLeft || thumb.offsetLeft + thumb.offsetWidth > this.thumbRail.scrollLeft + this.thumbRail.clientWidth)) this.thumbRail.scrollTo({ left: thumb.offsetLeft - this.thumbRail.clientWidth / 2 + thumb.offsetWidth / 2, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
      this.bubbles.forEach((bubble) => {
        const visible = Number(bubble.dataset.mediaNumber || 1) - 1 === index;
        bubble.classList.toggle('is-visible', visible);
        if (!visible) this.closeBubble(bubble);
      });
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.panels.length}`;
      this.refreshThumbnailNav?.();
      if (this.previous) this.previous.disabled = this.panels.length < 2;
      if (this.next) this.next.disabled = this.panels.length < 2;
      if (emitChange) {
        this.form?.dispatchEvent(new CustomEvent('wn:product:media-changed', {
          detail: { mediaId: activePanel?.dataset.mediaPanel || '', panel: activePanel }
        }));
      }
    }

    syncFromScroll() {
      if (!this.track || this.pointer?.dragged) return;
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      this.panels.forEach((panel, index) => {
        const nextDistance = Math.abs(center - (panel.offsetLeft + panel.offsetWidth / 2));
        if (nextDistance < distance) { distance = nextDistance; closest = index; }
      });
      if (closest !== this.activeIndex) this.sync(closest, true);
    }

    onPointerDown(event) {
      if (event.target.closest('button, a, [data-wn-bubble-panel]')) return;
      if (event.pointerType !== 'mouse') return;
      if (event.button !== 0) return;
      const image = event.target.closest('.wn-product-gallery__image');
      this.pointer = {
        id: event.pointerId,
        type: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        startScroll: this.track?.scrollLeft || 0,
        startIndex: this.activeIndex,
        image,
        dragged: false,
        captured: false
      };
      if (event.pointerType === 'mouse') {
        event.preventDefault();
        this.capturePointer(event.pointerId);
      }
    }

    capturePointer(pointerId) {
      if (!this.pointer || this.pointer.captured) return;
      try {
        this.surface.setPointerCapture?.(pointerId);
        this.pointer.captured = true;
      } catch { /* Pointer capture is optional. */ }
      this.surface.classList.add('is-dragging');
      if (this.track) this.track.style.scrollBehavior = 'auto';
    }

    onPointerMove(event) {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const dx = event.clientX - this.pointer.startX;
      const dy = event.clientY - this.pointer.startY;
      if (!this.pointer.dragged && Math.abs(dx) > 7 && Math.abs(dx) > Math.abs(dy) * 1.05) {
        this.pointer.dragged = true;
        this.capturePointer(event.pointerId);
      }
      if (!this.pointer.dragged) return;
      if (event.cancelable) event.preventDefault();
      if (this.track) this.track.scrollLeft = this.pointer.startScroll - dx;
    }

    onPointerUp(event) {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const pointer = this.pointer;
      const dx = event.clientX - pointer.startX;
      const dy = event.clientY - pointer.startY;
      const swipe = pointer.dragged && Math.abs(dx) >= 34 && Math.abs(dx) > Math.abs(dy) * 1.05;
      this.finishPointer();

      if (swipe) {
        this.suppressClickUntil = performance.now() + 350;
        this.go(pointer.startIndex + (dx < 0 ? 1 : -1));
        return;
      }

      if (pointer.dragged) {
        this.suppressClickUntil = performance.now() + 250;
        if (this.track) this.scrollToIndex(pointer.startIndex, true);
        this.sync(pointer.startIndex, false);
        return;
      }

      if (pointer.image && this.root.contains(pointer.image)) {
        if (event.cancelable) event.preventDefault();
        this.suppressClickUntil = performance.now() + 350;
        this.openLightbox();
      }
    }

    finishPointer() {
      const pointer = this.pointer;
      if (pointer?.captured) {
        try { this.surface.releasePointerCapture?.(pointer.id); } catch { /* no-op */ }
      }
      this.pointer = null;
      this.surface.classList.remove('is-dragging');
      if (this.track) this.track.style.removeProperty('scroll-behavior');
    }

    onClick(event) {
      const thumb = event.target.closest('[data-media-target]');
      if (thumb && this.root.contains(thumb)) {
        event.preventDefault();
        event.stopPropagation();
        if (performance.now() < (this.suppressThumbClickUntil || 0)) return;
        const index = this.indexForMedia(thumb.dataset.mediaTarget);
        if (index >= 0) this.go(index);
        return;
      }

      const image = event.target.closest('.wn-product-gallery__image');
      if (image && this.root.contains(image)) {
        event.preventDefault();
        if (performance.now() < this.suppressClickUntil) return;
        this.openLightbox();
        return;
      }

      const trigger = event.target.closest('[data-wn-bubble-trigger]');
      const close = event.target.closest('[data-wn-bubble-close]');
      if (!trigger && !close) return;
      event.preventDefault();
      event.stopPropagation();
      const bubble = event.target.closest('[data-wn-bubble]');
      if (!bubble) return;
      if (close) { this.closeBubble(bubble); return; }
      const open = bubble.classList.contains('is-open');
      this.closeBubbles();
      if (!open) {
        bubble.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        const panel = bubble.querySelector('[data-wn-bubble-panel]');
        if (panel) panel.hidden = false;
      }
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.activeIndex - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.activeIndex + 1); }
      if (event.key === 'Enter' && event.target === this.stage) { event.preventDefault(); this.openLightbox(); }
    }

    setupLightbox(signal) {
      const dialog = document.createElement('dialog');
      dialog.className = 'wn-gallery-lightbox';
      dialog.setAttribute('aria-label', window.WN_I18N?.product?.images || 'Product images');
      dialog.innerHTML = '<div class="wn-gallery-lightbox__frame"><button type="button" class="wn-gallery-lightbox__close" aria-label="Close">×</button><button type="button" class="wn-gallery-lightbox__arrow wn-gallery-lightbox__arrow--previous" aria-label="Previous image"><span aria-hidden="true">‹</span></button><figure><img class="wn-gallery-lightbox__image" alt=""><figcaption class="wn-gallery-lightbox__counter" aria-live="polite"></figcaption></figure><button type="button" class="wn-gallery-lightbox__arrow wn-gallery-lightbox__arrow--next" aria-label="Next image"><span aria-hidden="true">›</span></button></div>';
      document.body.append(dialog);
      this.lightbox = dialog;
      this.lightboxImage = dialog.querySelector('.wn-gallery-lightbox__image');
      this.lightboxCounter = dialog.querySelector('.wn-gallery-lightbox__counter');
      this.lightboxPrevious = dialog.querySelector('.wn-gallery-lightbox__arrow--previous');
      this.lightboxNext = dialog.querySelector('.wn-gallery-lightbox__arrow--next');
      dialog.querySelector('.wn-gallery-lightbox__close')?.addEventListener('click', () => this.closeLightbox(), { signal });
      this.lightboxPrevious?.addEventListener('click', () => this.go(this.activeIndex - 1), { signal });
      this.lightboxNext?.addEventListener('click', () => this.go(this.activeIndex + 1), { signal });
      dialog.addEventListener('click', (event) => {
        const protectedTarget = event.target.closest?.(
          '.wn-gallery-lightbox__image, .wn-gallery-lightbox__close, .wn-gallery-lightbox__arrow, .wn-gallery-lightbox__counter'
        );
        if (!protectedTarget) this.closeLightbox();
      }, { signal });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.activeIndex - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.activeIndex + 1); }
      }, { signal });
    }

    openLightbox() {
      if (!this.lightbox || !this.lightboxImage) return;
      this.updateLightbox();
      try {
        if (!this.lightbox.open && typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
        else if (!this.lightbox.open) this.lightbox.setAttribute('open', '');
      } catch {
        this.lightbox.setAttribute('open', '');
      }
      this.lightbox.querySelector('.wn-gallery-lightbox__close')?.focus({ preventScroll: true });
    }

    closeLightbox() {
      if (!this.lightbox) return;
      try {
        if (typeof this.lightbox.close === 'function' && this.lightbox.open) this.lightbox.close();
        else this.lightbox.removeAttribute('open');
      } catch { this.lightbox.removeAttribute('open'); }
    }

    updateLightbox() {
      const source = this.panels[this.activeIndex]?.querySelector('.wn-product-gallery__image');
      if (!source || !this.lightboxImage) return;
      this.lightboxImage.src = source.currentSrc || source.src;
      this.lightboxImage.alt = source.alt || '';
      if (this.lightboxCounter) this.lightboxCounter.textContent = `${this.activeIndex + 1} / ${this.panels.length}`;
      if (this.lightboxPrevious) this.lightboxPrevious.disabled = this.panels.length < 2;
      if (this.lightboxNext) this.lightboxNext.disabled = this.panels.length < 2;
    }

    closeBubble(bubble) {
      bubble.classList.remove('is-open');
      bubble.querySelector('[data-wn-bubble-trigger]')?.setAttribute('aria-expanded', 'false');
      const panel = bubble.querySelector('[data-wn-bubble-panel]');
      if (panel) panel.hidden = true;
    }

    closeBubbles() { this.bubbles.forEach((bubble) => this.closeBubble(bubble)); }

    destroy() {
      this.abortController.abort();
      cancelAnimationFrame(this.scrollFrame);
      this.finishPointer();
      this.lightbox?.remove();
      delete this.root.dataset.wnGalleryOwner;
      delete this.root.wnGalleryInstance;
    }
  }

  function ensureCareAnchor() {
    const care = document.querySelector('[data-wn-refill-care]');
    if (!care) return;
    if (!care.id) care.id = 'wn-filter-care';
    if (location.hash === '#wn-filter-care') requestAnimationFrame(() => care.scrollIntoView({ block: 'center' }));
  }

  function boot(scope = document) {
    const root = scope?.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.wnGalleryOwner === 'product-gallery-v3') return;
      gallery.wnGalleryInstance?.destroy?.();
      gallery.dataset.wnGalleryOwner = 'product-gallery-v3';
      gallery.wnGalleryInstance = new WnProductGallery(gallery);
    });
    ensureCareAnchor();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-product-gallery]').forEach((gallery) => gallery.wnGalleryInstance?.destroy());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
})();
